import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import EligibilityCard from '../components/EligibilityCard';
import EligibilityModal from '../components/EligibilityModal';
import ConfirmDialog from '../components/ConfirmDialog';

const HomeExtensionLoanEligibilityPage = () => {
    const [criteriaList, setCriteriaList] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Eligibility Criteria',
        section_subtitle: "Find out if you're eligible for Aham Home Extension Loan"
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCriteria, setEditingCriteria] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, criteriaId: null });

    const LOAN_TYPE = 'home_extension_loan';

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [criteriaRes, settingsRes] = await Promise.all([
                axios.get(`/api/admin/eligibility/${LOAN_TYPE}`),
                axios.get(`/api/admin/eligibility/settings/${LOAN_TYPE}`)
            ]);

            setCriteriaList(criteriaRes.data.data || []);
            if (settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load eligibility data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            // Ensure loan_type is sent
            const payload = { ...formData, loan_type: LOAN_TYPE };

            if (editingCriteria) {
                await axios.put(`/api/admin/eligibility/${editingCriteria.id}`, payload);
                toast.success('Criteria updated successfully');
            } else {
                await axios.post('/api/admin/eligibility', payload);
                toast.success('Criteria created successfully');
            }
            setIsModalOpen(false);
            setEditingCriteria(null);
            fetchData();
        } catch (error) {
            console.error('Error saving criteria:', error);
            toast.error('Failed to save criteria');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, criteriaId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/eligibility/${deleteConfirm.criteriaId}`);
            toast.success('Criteria deleted successfully');
            setDeleteConfirm({ isOpen: false, criteriaId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting criteria:', error);
            toast.error('Failed to delete criteria');
        }
    };

    // Handle settings update
    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put(`/api/admin/eligibility/settings/${LOAN_TYPE}`, settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Home Extension Loan Eligibility</h1>
                    <p className="text-slate-500 mt-1">Manage the eligibility requirements for Home Extension Loan</p>
                </div>
                <button
                    onClick={() => {
                        setEditingCriteria(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Criteria
                </button>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Eligibility Criteria"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Subtitle
                        </label>
                        <input
                            type="text"
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Find out if you're eligible..."
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={savingSettings}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Criteria Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="bg-white rounded-2xl h-48 shadow-sm animate-pulse border border-slate-100"></div>
                    ))}
                </div>
            ) : criteriaList.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <CheckBadgeIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No criteria added yet</h3>
                        <p className="text-slate-500 mb-6">Create eligibility criteria to showcase on the loan page.</p>
                        <button
                            onClick={() => {
                                setEditingCriteria(null);
                                setIsModalOpen(true);
                            }}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                        >
                            Add First Criteria
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {criteriaList.map((criteria) => (
                        <EligibilityCard
                            key={criteria.id}
                            criteria={criteria}
                            onEdit={(c) => {
                                setEditingCriteria(c);
                                setIsModalOpen(true);
                            }}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            <EligibilityModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingCriteria(null);
                }}
                onSubmit={handleSubmit}
                criteria={editingCriteria}
                loading={submitting}
                loanType={LOAN_TYPE}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, criteriaId: null })}
                onConfirm={confirmDelete}
                title="Delete Criteria"
                message="Are you sure you want to delete this criteria? This action cannot be undone."
            />
        </div>
    );
};

export default HomeExtensionLoanEligibilityPage;
